import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          theme?: 'light' | 'dark' | 'auto';
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove?: (widgetId: string) => void;
    };
  }
}

let turnstileScriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('cf-turnstile-script') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Turnstile')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'cf-turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
}

type TurnstileWidgetProps = {
  siteKey?: string;
  resetSignal?: number;
  onTokenChange: (token: string) => void;
};

export function TurnstileWidget({ siteKey, resetSignal = 0, onTokenChange }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    onTokenChange('');
    if (!siteKey) return;

    let disposed = false;

    loadTurnstileScript()
      .then(() => {
        if (disposed || !containerRef.current || !window.turnstile) return;

        containerRef.current.innerHTML = '';
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'light',
          callback: (token: string) => {
            if (!disposed) {
              setLoadError('');
              onTokenChange(token);
            }
          },
          'expired-callback': () => {
            if (!disposed) onTokenChange('');
          },
          'error-callback': () => {
            if (!disposed) {
              setLoadError('Turnstile failed to verify. Refresh and try again.');
              onTokenChange('');
            }
          },
        });
      })
      .catch(() => {
        if (!disposed) {
          setLoadError('Turnstile failed to load. Refresh and try again.');
          onTokenChange('');
        }
      });

    return () => {
      disposed = true;
      if (widgetIdRef.current && window.turnstile?.remove) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onTokenChange]);

  useEffect(() => {
    if (!siteKey || !widgetIdRef.current || !window.turnstile) return;
    onTokenChange('');
    window.turnstile.reset(widgetIdRef.current);
  }, [resetSignal, siteKey, onTokenChange]);

  if (!siteKey) return null;

  return (
    <div className="space-y-2">
      <div ref={containerRef} />
      {loadError && <p className="text-sm text-red-600">{loadError}</p>}
    </div>
  );
}
